import { useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import type { FinDashboard, FinPlLine } from '../../lib/financeTypes';
import { Banner, EmptyState, LoadingPage, Tag } from '../../components/ui';
import { IncomeExpenseChart } from './IncomeExpenseChart';
import { Metric, Segmented, usd } from './shared';

type Granularity = 'monthly' | 'quarterly' | 'annual';
type Fund = 'all' | 'operating' | 'replacement';

export function IncomeExpenses() {
  const [granularity, setGranularity] = useState<Granularity>('monthly');
  const [fund, setFund] = useState<Fund>('all');
  const dash = useAsync(() => api.finDashboard(granularity, fund), [granularity, fund]);
  const data = dash.data as FinDashboard | null;
  const reportId = data?.latest?.reportId;
  const detail = useAsync(
    async () => (reportId ? api.finReport(reportId) : null),
    [reportId],
  );

  if (!data) return dash.loading ? <LoadingPage label="Loading income and expenses…" /> : null;
  if (!data.hasData || !data.latest || !data.month || !data.ytd) {
    return <EmptyState title="No approved statement yet." hint="Income and expenses come from approved monthly statements." />;
  }

  const lines = (detail.data?.lines ?? []) as FinPlLine[];
  const shown = lines.filter((l) => fund === 'all' || l.fund === fund);
  const ytd = data.ytd;

  return (
    <div>
      <div className="row between" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <strong>{data.latest.periodLabel}</strong>
        <span className="mut">Year-to-date figures are read from the statement, never added across months.</span>
      </div>

      <div className="card">
        <div className="stat-grid">
          <Metric label="Operating revenue, YTD" value={usd(ytd.operatingRevenue)} hint={`Budget ${usd(ytd.budgetRevenue)}`} />
          <Metric label="Operating expenses, YTD" value={usd(ytd.operatingExpenses)} hint={`Budget ${usd(ytd.operatingBudgetExpenses)}`} />
          <Metric label="Operating surplus, YTD" value={usd(ytd.operatingSurplus)} tone={ytd.operatingSurplus < 0 ? 'bad' : 'ok'} />
          <Metric label="Replacement surplus, YTD" value={usd(ytd.replacementSurplus)} tone={ytd.replacementSurplus < 0 ? 'bad' : 'ok'} />
          <Metric label="This month, operating net" value={usd(data.month.operatingNet)} />
          <Metric
            label="Expense vs budget, this month"
            value={usd(data.month.operatingBudgetVariance)}
            hint={data.month.operatingBudgetVariance < 0 ? 'Over budget' : 'Under budget'}
            tone={data.month.operatingBudgetVariance < 0 ? 'bad' : 'ok'}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Income vs. expenses</h3>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Segmented
              label="Period"
              value={granularity}
              onChange={setGranularity}
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
                { value: 'annual', label: 'Annual' },
              ]}
            />
            <Segmented
              label="Fund"
              value={fund}
              onChange={setFund}
              options={[
                { value: 'all', label: 'Both funds' },
                { value: 'operating', label: 'Operating' },
                { value: 'replacement', label: 'Replacement' },
              ]}
            />
          </div>
        </div>
        {data.series && data.series.length > 0 ? (
          <IncomeExpenseChart points={data.series} granularity={granularity} />
        ) : (
          <p className="mut">No approved months to chart yet.</p>
        )}
        {data.seriesCoverage && (
          <p className="mut" style={{ marginBottom: 0 }}>
            Built from {data.seriesCoverage.months} approved statement{data.seriesCoverage.months === 1 ? '' : 's'} ({data.seriesCoverage.first}
            {data.seriesCoverage.months > 1 ? ` to ${data.seriesCoverage.last}` : ''}). Missing months are not estimated.
          </p>
        )}
      </div>

      <div className="card tw">
        <div className="card-head">
          <h3>Statement lines</h3>
          <span className="mut">Page numbers refer to the original statement</span>
        </div>
        {detail.loading && !detail.data ? (
          <p className="mut">Loading lines…</p>
        ) : shown.length === 0 ? (
          <p className="mut">No income or expense lines on this statement.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Line</th>
                <th className="num">Month</th>
                <th className="num">Month budget</th>
                <th className="num">YTD</th>
                <th className="num">YTD budget</th>
                <th className="num">Page</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((l) => (
                <tr key={l.id} style={l.kind === 'total' ? { fontWeight: 600 } : undefined}>
                  <td>
                    {l.label}
                    {l.fund === 'replacement' && <Tag>Replacement</Tag>}
                    {l.group && <div className="mut">{l.group}</div>}
                  </td>
                  <td className="num">{usd(l.monthActual, true)}</td>
                  <td className="num">{usd(l.monthBudget, true)}</td>
                  <td className="num">{usd(l.ytdActual, true)}</td>
                  <td className="num">{usd(l.ytdBudget, true)}</td>
                  <td className="num mut">{l.page}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data.warnings.length > 0 && data.warnings.map((w) => (
        <div key={w.message} style={{ marginTop: 8 }}>
          <Banner kind={w.level === 'warning' ? 'warn' : 'info'}>{w.message}</Banner>
        </div>
      ))}
    </div>
  );
}
